import React, { useEffect, useState } from "react";

const ImageGallery = ({ images }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const selectedImage = selectedIndex === null ? null : images[selectedIndex];

  const closeLightbox = () => setSelectedIndex(null);
  const showPrevious = () => {
    setSelectedIndex((current) => (current - 1 + images.length) % images.length);
  };
  const showNext = () => {
    setSelectedIndex((current) => (current + 1) % images.length);
  };

  useEffect(() => {
    if (selectedIndex === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelectedIndex(null);
      if (event.key === "ArrowLeft") {
        setSelectedIndex((current) => (current - 1 + images.length) % images.length);
      }
      if (event.key === "ArrowRight") {
        setSelectedIndex((current) => (current + 1) % images.length);
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedIndex, images.length]);

  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
        {images.map((image, index) => (
          <div
            key={image.id || image.src}
            className="rounded-lg overflow-hidden shadow hover:shadow-lg transition"
          >
            <button
              type="button"
              onClick={() => setSelectedIndex(index)}
              className="block w-full cursor-zoom-in focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-500"
              aria-label={`Enlarge ${image.alt}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-64 object-cover"
              />
            </button>
            {image.description && (
              <div className="p-4 bg-white">
                <p className="text-sm text-gray-700">{image.description}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 md:p-10"
          role="dialog"
          aria-modal="true"
          aria-label="Gallery image viewer"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeLightbox();
          }}
        >
          <button
            type="button"
            onClick={closeLightbox}
            autoFocus
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-3xl text-white hover:bg-black focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-400"
            aria-label="Close image viewer"
          >
            &times;
          </button>

          {images.length > 1 && (
            <button
              type="button"
              onClick={showPrevious}
              className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-4xl text-white hover:bg-black focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-400 md:left-6"
              aria-label="Previous image"
            >
              &#8249;
            </button>
          )}

          <figure className="flex max-h-full max-w-full flex-col items-center">
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-h-[82vh] max-w-[92vw] object-contain"
            />
            {(selectedImage.title || selectedImage.description) && (
              <figcaption className="mt-3 max-w-3xl text-center text-white">
                {selectedImage.title && (
                  <div className="font-semibold">{selectedImage.title}</div>
                )}
                {selectedImage.description && (
                  <div className="mt-1 text-sm text-gray-200">
                    {selectedImage.description}
                  </div>
                )}
              </figcaption>
            )}
          </figure>

          {images.length > 1 && (
            <button
              type="button"
              onClick={showNext}
              className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-4xl text-white hover:bg-black focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-400 md:right-6"
              aria-label="Next image"
            >
              &#8250;
            </button>
          )}

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-sm text-gray-300">
            {selectedIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;
